import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { NumberBlock } from './rufnummer.model';
import { RufnummerService } from './rufnummer.service';

export interface AttachNumberBlocksModalData {
  brandId: number;
  brandName: string;
}

@Component({
  selector: 'cmt-attach-number-blocks-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
  ],
  templateUrl: './rufnummer-modal.component.html',
})
export class AttachNumberBlocksModalComponent {
  readonly data = inject<AttachNumberBlocksModalData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<AttachNumberBlocksModalComponent>);
  private readonly service = inject(RufnummerService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly searchText = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  protected readonly allBlocks = signal<NumberBlock[]>([]);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  protected readonly selectedKeys = signal<ReadonlySet<string>>(new Set());
  protected readonly attaching = signal(false);

  /**
   * Case-insensitive substring filter across vorwahl/prefix/carrier/staOrt/staLand.
   * Multi-token (whitespace-separated) → all tokens must match somewhere in the
   * concatenated row text. Empty input passes everything.
   */
  protected readonly filtered = computed(() => {
    const text = this.searchText().trim().toLowerCase();
    const all = this.allBlocks();
    if (!text) return all;
    const tokens = text.split(/\s+/).filter(Boolean);
    return all.filter(b => {
      const hay = `${b.vorwahl} ${b.prefix} ${b.carrier ?? ''} ${b.staOrt ?? ''} ${b.staLand ?? ''}`.toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
  });

  protected readonly selectedCount = computed(() => this.selectedKeys().size);
  protected readonly canSubmit = computed(
    () => this.selectedCount() > 0 && !this.attaching(),
  );

  /**
   * Tri-state for the select-all header checkbox, scoped to the **filtered**
   * rows (never the full universe — the user only sees what they're picking).
   * - 'none'  : zero filtered rows are selected
   * - 'all'   : every filtered row is selected
   * - 'some'  : at least one but not all are selected → indeterminate
   */
  protected readonly filteredSelectionState = computed<'none' | 'some' | 'all'>(() => {
    const visible = this.filtered();
    if (visible.length === 0) return 'none';
    const selected = this.selectedKeys();
    let hits = 0;
    for (const b of visible) if (selected.has(b.key)) hits++;
    if (hits === 0) return 'none';
    if (hits === visible.length) return 'all';
    return 'some';
  });

  constructor() {
    this.service.listAvailable(this.data.brandId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.allBlocks.set(list);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.loadFailed.set(true);
          this.toast.open('Could not load available blocks. Try again.', 'OK', { duration: 4000, tone: 'error' });
        },
      });
  }

  protected toggle(key: string): void {
    this.selectedKeys.update(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  protected isSelected(key: string): boolean {
    return this.selectedKeys().has(key);
  }

  /**
   * Header checkbox handler. If anything in the filtered set is unselected,
   * select-all-visible (additive — doesn't disturb selections from a previous
   * filter). Otherwise clear the visible selections only.
   */
  protected toggleSelectAllFiltered(): void {
    const visible = this.filtered();
    if (visible.length === 0) return;
    const state = this.filteredSelectionState();
    this.selectedKeys.update(prev => {
      const next = new Set(prev);
      if (state === 'all') {
        for (const b of visible) next.delete(b.key);
      } else {
        for (const b of visible) next.add(b.key);
      }
      return next;
    });
  }

  protected attachSelected(): void {
    const selected = this.selectedKeys();
    const picks = this.allBlocks().filter(b => selected.has(b.key));
    if (picks.length === 0) return;
    this.attaching.set(true);
    this.service.attach(this.data.brandId, picks)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: inserted => {
          this.attaching.set(false);
          const n = inserted.length;
          if (n === 0) {
            this.toast.open('Already linked — nothing new added.', 'OK', { duration: 3000, tone: 'default' });
          } else {
            this.toast.open(
              `${n === 1 ? '1 block' : `${n} blocks`} linked`,
              'OK',
              { duration: 3000 },
            );
          }
          this.dialogRef.close(true);
        },
        error: () => {
          this.attaching.set(false);
          this.toast.open('Failed to link. Try again.', 'OK', { duration: 5000 });
        },
      });
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
